import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Alert,
  Paper,
  Skeleton,
  Chip,
  Avatar,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery,
  alpha,
  Stack,
  Divider,
  Badge,
} from '@mui/material';
import {
  Person as PersonIcon,
  EmergencyRecording as EmergencyIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Lock as LockIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  LocalHospital as MedicalIcon,
  AccountBalance as BankIcon,
  ContactPhone as ContactIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Security as SecurityIcon,
  TrendingUp as TrendingUpIcon,
  Shield as ShieldIcon,
  Speed as SpeedIcon,
  Notes as NotesIcon,
} from '@mui/icons-material';
import { authService } from '../services/authService';
import { emergencyService } from '../services/emergencyService';
import { User, EmergencyInfo } from '../types';
import PasswordManager from '../utils/passwordManager';
import PrimaryButton from '../components/PrimaryButton';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [user, setUser] = useState<User | null>(null);
  const [emergencyInfo, setEmergencyInfo] = useState<EmergencyInfo | null>(null);
  const [hasEmergencyInfo, setHasEmergencyInfo] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);

  useEffect(() => {
    loadUserData();
    
    // Actualizar contador cada segundo
    const interval = setInterval(() => {
      setTimeRemaining(PasswordManager.getTimeRemaining());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const loadUserData = async () => {
    try {
      const userData = await authService.getCurrentUser();
      setUser(userData);
      
      // Verificar si tenemos contraseña válida
      const savedPassword = PasswordManager.getTemporaryPassword();
      if (savedPassword) {
        await loadEmergencyInfo(savedPassword);
      } else {
        // Solo verificar si existe información sin cargarla
        setHasEmergencyInfo(null);
      }
    } catch (err) {
      setError('Error al cargar los datos del usuario');
    } finally {
      setLoading(false);
    }
  };

  const loadEmergencyInfo = async (pwd: string) => {
    try {
      const info = await emergencyService.getEmergencyInfo(pwd);
      setEmergencyInfo(info);
      setHasEmergencyInfo(true);
      setTimeRemaining(PasswordManager.getTimeRemaining());
    } catch (err: any) {
      if (err.response?.status === 404) {
        setHasEmergencyInfo(false);
        setEmergencyInfo(null);
      } else {
        setError('Error al cargar la información de emergencia');
      }
    }
  };

  const handlePasswordSubmit = async () => {
    if (!password) {
      setError('La contraseña es requerida');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Guardar contraseña con timeout de 10 minutos
      PasswordManager.setTemporaryPassword(password, 600000);
      await loadEmergencyInfo(password);
      setPasswordDialog(false);
      setPassword('');
    } catch (err: any) {
      PasswordManager.clearTemporaryPassword();
      if (err.response?.status === 404) {
        setHasEmergencyInfo(false);
        setPasswordDialog(false);
        setPassword('');
      } else {
        setError('Contraseña incorrecta o error al cargar la información');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewInfo = () => {
    if (PasswordManager.hasValidPassword()) {
      navigate('/emergency-info');
    } else {
      setPasswordDialog(true);
    }
  };

  const extendSession = () => {
    PasswordManager.extendSession(600000); // Extender 10 minutos más
    setTimeRemaining(PasswordManager.getTimeRemaining());
  };

  const getCompletionInfo = () => {
    if (!emergencyInfo) return { completed: 0, total: 0, percentage: 0, preview: {} };
    
    let completed = 0;
    let total = 6; // Total de secciones principales
    const preview: any = {};

    if (emergencyInfo.phone_numbers && emergencyInfo.phone_numbers.length > 0) {
      completed++;
      preview.phones = emergencyInfo.phone_numbers.slice(0, 2).map(p => `${p.name}: ${p.number}`);
    }
    if (emergencyInfo.addresses && emergencyInfo.addresses.length > 0) {
      completed++;
      preview.addresses = emergencyInfo.addresses.slice(0, 2).map(a => `${a.type}: ${a.street}, ${a.city}`);
    }
    if (emergencyInfo.emergency_contacts && emergencyInfo.emergency_contacts.length > 0) {
      completed++;
      preview.contacts = emergencyInfo.emergency_contacts.slice(0, 2).map(c => `${c.name} (${c.relationship}): ${c.phone}`);
    }
    if (emergencyInfo.medical_info && emergencyInfo.medical_info.blood_type) {
      completed++;
      preview.medical = `Tipo de sangre: ${emergencyInfo.medical_info.blood_type}`;
    }
    if (emergencyInfo.financial_info && emergencyInfo.financial_info.bank_name) {
      completed++;
      preview.financial = `${emergencyInfo.financial_info.bank_name}${emergencyInfo.financial_info.account_type ? ` - ${emergencyInfo.financial_info.account_type}` : ''}`;
    }
    if (emergencyInfo.additional_notes) {
      completed++;
      preview.notes = emergencyInfo.additional_notes.substring(0, 100) + (emergencyInfo.additional_notes.length > 100 ? '...' : '');
    }

    return {
      completed,
      total,
      percentage: Math.round((completed / total) * 100),
      preview
    };
  };

  if (loading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={isMobile ? 150 : 200} sx={{ mb: 3, borderRadius: 3 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rectangular" height={isMobile ? 120 : 150} sx={{ borderRadius: 3 }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rectangular" height={isMobile ? 120 : 150} sx={{ borderRadius: 3 }} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  const completionInfo = getCompletionInfo();

  return (
    <Box sx={{ pb: { xs: 2, md: 4 } }}>
      {/* Header mejorado con información del usuario */}
      <Paper
        sx={{
          p: { xs: 3, md: 4 },
          mb: { xs: 3, md: 4 },
          background: 'var(--primary-gradient)',
          color: 'white',
          borderRadius: 4,
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: '40%',
            height: '100%',
            background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 100%)',
            borderRadius: '50% 0 0 50%',
          },
        }}
      >
        <Grid container alignItems="center" spacing={3}>
          <Grid item>
            <Avatar
              sx={{
                width: { xs: 70, md: 90 },
                height: { xs: 70, md: 90 },
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                fontSize: { xs: '1.8rem', md: '2.5rem' },
                fontWeight: 'bold',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
              }}
            >
              {user?.full_name ? user.full_name[0].toUpperCase() : <PersonIcon sx={{ fontSize: 'inherit' }} />}
            </Avatar>
          </Grid>
          <Grid item xs>
            <Typography 
              variant={isMobile ? "h4" : "h3"} 
              fontWeight="800" 
              gutterBottom
              sx={{ 
                lineHeight: 1.1,
                textShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
                fontSize: { xs: '1.8rem', md: '2.5rem' },
              }}
            >
              ¡Hola{user?.full_name ? `, ${user.full_name}` : ''}! 👋
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 1 }}>
              <Chip
                icon={<PersonIcon />}
                label={`RUT: ${user?.rut}`}
                sx={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  fontWeight: 600,
                  '& .MuiChip-icon': { color: 'white' },
                }}
              />
              <Chip
                icon={<LockIcon />}
                label="Cuenta Segura"
                sx={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  fontWeight: 600,
                  '& .MuiChip-icon': { color: 'white' },
                }}
              />
            </Stack>
            <Typography 
              variant="body1" 
              sx={{ 
                opacity: 0.9,
                fontSize: { xs: '0.95rem', md: '1.1rem' },
                fontWeight: 500,
              }}
            >
              📧 {user?.email}
            </Typography>
          </Grid>
          <Grid item>
            <PrimaryButton
              variant="outlined"
              startIcon={<SecurityIcon />}
              onClick={() => navigate('/account-settings')}
              sx={{
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.5)',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                fontWeight: 600,
                '&:hover': {
                  borderColor: 'white',
                  background: 'rgba(255, 255, 255, 0.2)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
                },
              }}
            >
              {isMobile ? 'Config.' : 'Configurar Cuenta'}
            </PrimaryButton>
          </Grid>
        </Grid>

        {/* Información de sesión activa mejorada */}
        {timeRemaining > 0 && (
          <Box sx={{ 
            mt: 3, 
            p: 2, 
            background: 'rgba(255, 255, 255, 0.1)', 
            backdropFilter: 'blur(10px)',
            borderRadius: 2,
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <TimeIcon sx={{ color: 'rgba(255, 255, 255, 0.9)' }} />
                <Typography variant="body2" fontWeight={600}>
                  🔓 Sesión activa: {Math.floor(timeRemaining / 60)}:{String(timeRemaining % 60).padStart(2, '0')}
                </Typography>
              </Box>
              <PrimaryButton
                size="small"
                variant="outlined"
                onClick={extendSession}
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  background: 'rgba(255, 255, 255, 0.1)',
                  '&:hover': {
                    borderColor: 'white',
                    background: 'rgba(255, 255, 255, 0.2)',
                  },
                }}
              >
                ⏰ Extender
              </PrimaryButton>
            </Box>
          </Box>
        )}
      </Paper>

      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3, 
            borderRadius: 3,
            '& .MuiAlert-message': { fontSize: '1rem' },
          }}
        >
          {error}
        </Alert>
      )}

      {/* Resumen de información mejorado */}
      {emergencyInfo && (
        <Paper 
          sx={{ 
            p: { xs: 3, md: 4 }, 
            mb: { xs: 3, md: 4 },
            borderRadius: 4,
            background: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
            <Box>
              <Typography 
                variant="h5" 
                fontWeight="700" 
                sx={{ 
                  fontSize: { xs: '1.3rem', md: '1.5rem' },
                  background: 'var(--primary-gradient)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  mb: 1,
                }}
              >
                📋 Tu Información de Emergencia
              </Typography>
              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <Chip 
                  label={`${completionInfo.completed}/${completionInfo.total} secciones completadas`} 
                  color={completionInfo.percentage === 100 ? "success" : "primary"}
                  sx={{ 
                    fontWeight: 600,
                    ...(completionInfo.percentage === 100 && {
                      color: 'white',
                      '& .MuiChip-label': {
                        color: 'white',
                      },
                    }),
                  }}
                />
                <Chip 
                  label={`${completionInfo.percentage}% completo`} 
                  variant="outlined"
                  color={completionInfo.percentage === 100 ? "success" : "warning"}
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            </Box>
            <PrimaryButton
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleViewInfo}
              sx={{
                px: 3,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 25px rgba(102, 126, 234, 0.4)',
                },
              }}
            >
              Gestionar Información
            </PrimaryButton>
          </Box>
          
          <LinearProgress 
            variant="determinate" 
            value={completionInfo.percentage} 
            sx={{ 
              mb: 3, 
              height: 12, 
              borderRadius: 6,
              background: alpha(theme.palette.grey[300], 0.3),
              '& .MuiLinearProgress-bar': {
                borderRadius: 6,
                background: completionInfo.percentage === 100 
                  ? 'linear-gradient(45deg, #4caf50 30%, #66bb6a 90%)'
                  : 'var(--primary-gradient)',
              },
            }}
          />
          
          <Grid container spacing={3}>
            {/* Tarjetas de información mejoradas */}
            {[
              {
                icon: PhoneIcon,
                title: 'Teléfonos',
                count: emergencyInfo.phone_numbers?.length || 0,
                data: completionInfo.preview.phones,
                color: '#2196f3',
                emoji: '📞',
              },
              {
                icon: HomeIcon,
                title: 'Direcciones',
                count: emergencyInfo.addresses?.length || 0,
                data: completionInfo.preview.addresses,
                color: '#ff9800',
                emoji: '🏠',
              },
              {
                icon: ContactIcon,
                title: 'Contactos de Emergencia',
                count: emergencyInfo.emergency_contacts?.length || 0,
                data: completionInfo.preview.contacts,
                color: '#f44336',
                emoji: '🚨',
              },
              {
                icon: MedicalIcon,
                title: 'Información Médica',
                count: emergencyInfo.medical_info?.blood_type ? 1 : 0,
                data: completionInfo.preview.medical,
                color: '#4caf50',
                emoji: '🏥',
              },
              {
                icon: BankIcon,
                title: 'Información Financiera',
                count: emergencyInfo.financial_info?.bank_name ? 1 : 0,
                data: completionInfo.preview.financial,
                color: '#9c27b0',
                emoji: '💳',
              },
              {
                icon: NotesIcon,
                title: 'Notas Adicionales',
                count: emergencyInfo.additional_notes ? 1 : 0,
                data: completionInfo.preview.notes,
                color: '#607d8b',
                emoji: '📝',
              },
            ].map((item, index) => (
              <Grid item xs={12} sm={6} lg={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    background: alpha(theme.palette.background.paper, 0.7),
                    backdropFilter: 'blur(10px)',
                    border: `1px solid ${alpha(item.color, 0.2)}`,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 8px 30px ${alpha(item.color, 0.2)}`,
                      border: `1px solid ${alpha(item.color, 0.4)}`,
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: 4,
                      background: item.color,
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            background: alpha(item.color, 0.1),
                            border: `1px solid ${alpha(item.color, 0.2)}`,
                          }}
                        >
                          <item.icon sx={{ color: item.color, fontSize: 24 }} />
                        </Box>
                        <Typography variant="h6" fontWeight="600" sx={{ fontSize: '1rem' }}>
                          {item.emoji} {item.title}
                        </Typography>
                      </Box>
                      <Badge
                        badgeContent={item.count}
                        color={item.count > 0 ? "success" : "default"}
                        sx={{
                          '& .MuiBadge-badge': {
                            fontSize: '0.75rem',
                            fontWeight: 600,
                          },
                        }}
                      >
                        {item.count > 0 ? (
                          <CheckIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                        ) : (
                          <WarningIcon sx={{ color: '#ff9800', fontSize: 20 }} />
                        )}
                      </Badge>
                    </Box>
                    
                    <Divider sx={{ mb: 2, opacity: 0.5 }} />
                    
                    {item.data ? (
                      <Box>
                        {Array.isArray(item.data) ? (
                          item.data.map((dataItem: string, idx: number) => (
                            <Typography 
                              key={idx} 
                              variant="body2" 
                              sx={{ 
                                mb: 0.5,
                                color: 'text.secondary',
                                fontWeight: 500,
                                fontSize: '0.875rem',
                              }}
                            >
                              • {dataItem}
                            </Typography>
                          ))
                        ) : (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: 'text.secondary',
                              fontWeight: 500,
                              fontSize: '0.875rem',
                            }}
                          >
                            • {item.data}
                          </Typography>
                        )}
                        {item.count > 2 && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: item.color,
                              fontWeight: 600,
                              fontSize: '0.75rem',
                            }}
                          >
                            +{item.count - 2} más registros...
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'text.disabled',
                          fontStyle: 'italic',
                          fontSize: '0.875rem',
                        }}
                      >
                        ⚠️ Sin información registrada
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Cards de consejos mejoradas */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              height: '100%',
              borderRadius: 4,
              background: `linear-gradient(135deg, 
                ${alpha('#ff9800', 0.1)} 0%, 
                ${alpha('#ffb74d', 0.05)} 100%)`,
              border: `1px solid ${alpha('#ff9800', 0.2)}`,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 30px rgba(255, 152, 0, 0.2)',
              },
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Typography 
                variant="h5" 
                fontWeight="700"
                sx={{
                  mb: 3,
                  fontSize: { xs: '1.3rem', md: '1.4rem' },
                  background: 'linear-gradient(45deg, #ff9800 30%, #ffb74d 90%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                💡 Consejos de Seguridad
              </Typography>
              <Stack spacing={2.5}>
                {[
                  { icon: '🧠', title: 'Memoriza tu contraseña', desc: 'O guárdala en un lugar seguro físico' },
                  { icon: '🤐', title: 'No la compartas', desc: 'Tu contraseña debe ser solo tuya' },
                  { icon: '🔄', title: 'Mantén actualizada', desc: 'Revisa tu información regularmente' },
                  { icon: '⚠️', title: 'Importante', desc: 'Sin tu contraseña, ni nosotros podemos recuperar tu información' },
                ].map((tip, idx) => (
                  <Box key={idx} display="flex" gap={2} alignItems="flex-start">
                    <Typography variant="h6" sx={{ fontSize: '1.5rem', minWidth: 'auto' }}>
                      {tip.icon}
                    </Typography>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 0.5 }}>
                        {tip.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                        {tip.desc}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              height: '100%',
              borderRadius: 4,
              background: `linear-gradient(135deg, 
                ${alpha('#4caf50', 0.1)} 0%, 
                ${alpha('#66bb6a', 0.05)} 100%)`,
              border: `1px solid ${alpha('#4caf50', 0.2)}`,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 30px rgba(76, 175, 80, 0.2)',
              },
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Typography 
                variant="h5" 
                fontWeight="700"
                sx={{
                  mb: 3,
                  fontSize: { xs: '1.3rem', md: '1.4rem' },
                  background: 'linear-gradient(45deg, #4caf50 30%, #66bb6a 90%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                🛡️ Tu Información está Protegida
              </Typography>
              <Stack spacing={2.5}>
                {[
                  { icon: '🔐', title: 'Cifrado extremo a extremo', desc: 'Solo tú puedes ver tu información' },
                  { icon: '🔑', title: 'Tu contraseña es tu llave', desc: 'Sin ella, nadie puede acceder a tus datos' },
                  { icon: '🚀', title: 'Acceso rápido', desc: 'Solo necesitas tu RUT y contraseña' },
                  { icon: '✅', title: 'Sin respaldos no cifrados', desc: 'Tu privacidad es nuestra prioridad' },
                ].map((feature, idx) => (
                  <Box key={idx} display="flex" gap={2} alignItems="flex-start">
                    <Typography variant="h6" sx={{ fontSize: '1.5rem', minWidth: 'auto' }}>
                      {feature.icon}
                    </Typography>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 0.5 }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                        {feature.desc}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* CTA para usuarios sin información */}
      {hasEmergencyInfo === false && (
        <Paper 
          sx={{ 
            p: { xs: 4, md: 5 }, 
            mt: 4,
            textAlign: 'center',
            background: 'var(--primary-gradient)',
            color: 'white',
            borderRadius: 4,
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -50,
              right: -50,
              width: 100,
              height: 100,
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.1)',
              animation: 'pulse 3s infinite',
            },
          }}
        >
          <Box
            sx={{
              width: { xs: 80, md: 100 },
              height: { xs: 80, md: 100 },
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
              border: '2px solid rgba(255, 255, 255, 0.3)',
            }}
          >
            <EmergencyIcon sx={{ fontSize: { xs: 40, md: 50 }, color: 'white' }} />
          </Box>
          <Typography 
            variant="h4" 
            fontWeight="800" 
            gutterBottom
            sx={{ 
              fontSize: { xs: '1.5rem', md: '2rem' },
              textShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
            }}
          >
            🚨 ¡Configura tu Información de Emergencia!
          </Typography>
          <Typography 
            variant="h6" 
            paragraph 
            sx={{ 
              opacity: 0.9,
              fontSize: { xs: '1rem', md: '1.1rem' },
              maxWidth: 600,
              mx: 'auto',
              mb: 4,
            }}
          >
            Guarda números telefónicos, direcciones, información médica y más.
            Accede a toda tu información vital cuando más lo necesites.
          </Typography>
          <PrimaryButton
            variant="outlined"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => navigate('/emergency-info')}
            sx={{
              color: 'white',
              borderColor: 'rgba(255, 255, 255, 0.7)',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: 3,
              '&:hover': {
                borderColor: 'white',
                background: 'rgba(255, 255, 255, 0.2)',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
              },
            }}
          >
            🚀 Configurar Ahora
          </PrimaryButton>
        </Paper>
      )}

      {/* Dialog para contraseña mejorado */}
      <Dialog 
        open={passwordDialog} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: alpha(theme.palette.background.paper, 0.95),
            backdropFilter: 'blur(20px)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                background: alpha(theme.palette.primary.main, 0.1),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <LockIcon color="primary" />
            </Box>
            <Typography variant="h6" fontWeight="600">
              🔐 Verificar Contraseña
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body1" color="text.secondary" paragraph>
            Para ver tu información de emergencia, necesitamos verificar tu contraseña.
          </Typography>
          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            label="Contraseña"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ 
              mt: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <PrimaryButton 
            onClick={() => setPasswordDialog(false)}
            variant="outlined"
          >
            Cancelar
          </PrimaryButton>
          <PrimaryButton 
            onClick={handlePasswordSubmit}
            disabled={!password}
          >
            🔓 Verificar
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard; 
